import { useEffect, useMemo, useState, ChangeEvent, useRef, useId } from "react";
import { Button, Slider, Switch, SvgIcon, Tab, Tabs } from "@mui/material";

// The dialog was extracted verbatim from assistants.view.tsx (2026-10-05) so
// the left menu can open it for the current Assistant; it keeps using the
// Assistants page stylesheet.
import "../../../views/admin/assistants/style.scss";
import { ReactComponent as CopyIcon } from "../../../assets/copy.icon.svg";
import {
    fetchKnowledgeBaseConfig,
    fetchKnowledgeBaseInvites,
    fetchKBLogoUrl,
    patchKnowledgeBase,
    patchKnowledgeBaseConfig
} from "../../../api/admin/knowledge-base/endpoints-referrers";
import { fetchEmbeddingModels } from "../../../api/admin/config/endpoints-referrers";
import { uploadFile } from "../../../api/admin/content-providers/endpoints-referrers";
import { KnowledgeBase } from "../../../common/interfaces/admin/knowledge-base.interface";
import { Invite } from "../../../common/interfaces/admin/invite-interface";
import { UserRole } from "../../../common/enums/user-role.enum";
import { trim } from "../../../common/utilities/text/text-formatter.util";
import { ToastContainer } from "../../toast/toast.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../confirmation-popup/confirmation-popup.comp";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { DialField, DialHelpProvider, DialHelpStrip } from "../../dial-field/dial-field.comp";

// ---------------------------------------------------------------------------
// Edit dialog: the tabbed absorption of the retired per-Assistant pages
// (assistants_agents_separation_design.md §3.2). One Save patches only the
// changed field groups.
// ---------------------------------------------------------------------------

const RERANKER_MODELS = [
    "gpt-5.4-mini",
    "gpt-5.6-luna",
    "gpt-5.6-terra",
    "gpt-6-luna",
    "gpt-6-sol",
    "claude-haiku-5-5",
    "gemini-3.7-flash",
    "gemini-3.8-flash"
];

// Key-order-stable normalization so the dirty check compares like with like.
const normalizeProcessingOptions = (o: any): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    if (o?.metadataExtractionModel) out.metadataExtractionModel = o.metadataExtractionModel;
    if (typeof o?.metadataSamplePages === "number") out.metadataSamplePages = o.metadataSamplePages;
    if (typeof o?.redigestBatchSize === "number") out.redigestBatchSize = o.redigestBatchSize;
    if (typeof o?.videoChunkSeconds === "number") out.videoChunkSeconds = o.videoChunkSeconds;
    if (o?.videoTranscriptionModel) out.videoTranscriptionModel = o.videoTranscriptionModel;
    return out;
};

// Mirrors backend METADATA_EXTRACTION_MODELS — the API rejects anything else.
const METADATA_MODELS = [
    "gemini-3.7-flash",
    "gemini-3.6-flash",
    "gemini-3.5-flash",
    "gemini-3.5-flash-lite",
    "gemini-3.1-flash-lite",
    "gemini-2.5-flash",
    "gemini-2.5-flash-lite"
];

const PDF_MODELS = [
    "gemini-3.6-flash",
    "gemini-3.5-flash",
    "gemini-3.5-flash-lite",
    "gemini-3.1-flash-lite",
    "gemini-3.1-pro-preview",
    "gemini-3-flash-preview",
    "gemini-2.5-pro",
    "gemini-2.5-flash",
    "gemini-2.5-flash-lite"
];

// Mirrors backend VIDEO_TRANSCRIPTION_MODELS — the API rejects anything else.
const VIDEO_MODELS = ["gemini-3-flash-preview", "gemini-3.7-flash", "gemini-3.8-flash"];

// Gemini thinking levels (thinking_level replaced thinking_budget, Google
// notice 2026-10-08). Support varies by model; the flow service clamps to the
// nearest level the chosen model accepts.
const THINKING_LEVELS = [
    { value: "minimal", label: "Minimal" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" }
];

const BM25_LANGUAGES = [
    "arabic",
    "danish",
    "dutch",
    "english",
    "finnish",
    "french",
    "german",
    "greek",
    "hungarian",
    "indonesian",
    "irish",
    "italian",
    "lithuanian",
    "nepali",
    "norwegian",
    "portuguese",
    "romanian",
    "russian",
    "spanish",
    "swedish",
    "tamil",
    "turkish"
];

const SUGGESTED_QUESTION_LIMITS = [250, 250, 250, 250, 250, 250];

/**
 * Which feature areas this user may edit — role gates and tenant admin
 * permissions already combined by the caller (§6). A tab renders only when
 * something on it is permitted; Processing renders per permitted section.
 */
export interface AssistantDialogFeatures {
    prompt: boolean;
    retrieval: boolean;
    recognition: boolean;
    chunking: boolean;
    metadata: boolean;
    maintenance: boolean;
    embedding: boolean;
    invites: boolean;
}

export function AssistantEditDialog({
    companyId,
    assistant,
    features,
    initialTab,
    onClose,
    onSaved
}: {
    companyId: number;
    assistant: KnowledgeBase;
    features: AssistantDialogFeatures;
    initialTab: string | null;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { showToast } = ToastContainer.useContainer();
    const fieldId = useId();
    // aria-describedby target for the dial help strip (settings-dial standard).
    const dialHelpId = useId();
    // Tab gates mirror the server guards (§8.2 + tenant permissions §6): a
    // tab renders only when something on it is permitted for this user.
    const anyProcessing =
        features.recognition ||
        features.chunking ||
        features.metadata ||
        features.maintenance ||
        features.embedding;
    const tabs = useMemo(
        () => [
            "profile",
            "presentation",
            "answers",
            ...(features.prompt ? ["prompt"] : []),
            ...(features.retrieval ? ["retrieval"] : []),
            ...(anyProcessing ? ["processing"] : []),
            ...(features.invites ? ["invites"] : [])
        ],
        [features, anyProcessing]
    );
    const [tab, setTab] = useState<string>(
        initialTab && tabs.includes(initialTab) ? initialTab : "profile"
    );
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmEmbedding, setConfirmEmbedding] = useState(false);

    // Profile
    const [displayName, setDisplayName] = useState(assistant.displayName ?? "");
    const [description, setDescription] = useState(assistant.description ?? "");
    const [logoFile, setLogoFile] = useState<File | undefined>();
    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const [removeLogo, setRemoveLogo] = useState(false);
    // Presentation
    const [assistantTitle, setAssistantTitle] = useState("");
    const [assistantDescription, setAssistantDescription] = useState("");
    const [questions, setQuestions] = useState<string[]>(Array(6).fill(""));
    const [negativeAnswer, setNegativeAnswer] = useState("");
    const [responseInstructions, setResponseInstructions] = useState("");
    // Coverage description: authored asset-manifest text the Coordinator's
    // routing digest embeds; the generated text serves as the placeholder.
    const [coverageDescription, setCoverageDescription] = useState("");
    const [coverageGenerated, setCoverageGenerated] = useState("");
    // Prompt
    // Seeded from the config GET (raw stored value, empty when unset) — the
    // assistant prop can't be trusted for this: summary rows omit the prompt
    // and the auth container's entity carries a platform-defaulted one.
    const [prompt, setPrompt] = useState("");
    // Retrieval
    const [vectorWeight, setVectorWeight] = useState(0.5);
    const bm25Weight = Math.round((1 - vectorWeight) * 10) / 10;
    const [searchTopK, setSearchTopK] = useState(50);
    const [searchResultLimit, setSearchResultLimit] = useState(10);
    const [rrfConstant, setRrfConstant] = useState(60);
    const [useReranker, setUseReranker] = useState(true);
    const [rerankerModel, setRerankerModel] = useState<string | null>(null);
    const [bm25Language, setBm25Language] = useState("english");
    // Processing
    const [chunkSize, setChunkSize] = useState<number | undefined>();
    const [chunkOverlap, setChunkOverlap] = useState<number | undefined>();
    const [recognitionMode, setRecognitionMode] = useState<string | null>(null);
    const [pdfRecognitionModel, setPdfRecognitionModel] = useState<string | null>(null);
    // Gemini thinking level for recognition/transcription; "" = model default.
    const [thinkingLevel, setThinkingLevel] = useState<string>("");
    const [embeddingModel, setEmbeddingModel] = useState<string>("");
    const [embeddingModels, setEmbeddingModels] = useState<string[]>([]);
    // Phase B processing dials (processing_options JSONB) — unset = default.
    const [metadataModel, setMetadataModel] = useState<string>("");
    const [metadataSamplePages, setMetadataSamplePages] = useState<number | undefined>();
    const [redigestBatchSize, setRedigestBatchSize] = useState<number | undefined>();
    const [videoChunkSeconds, setVideoChunkSeconds] = useState<number | undefined>();
    const [videoModel, setVideoModel] = useState<string>("");
    // Invites
    const [invites, setInvites] = useState<Invite[]>([]);

    const initial = useRef<Record<string, unknown>>({});

    useEffect(() => {
        (async () => {
            try {
                // Invites are permission-gated server-side (D12 + inviteLinks)
                // — an unpermitted fetch would 403 and sink the whole load.
                const [cfgRes, invRes] = await Promise.all([
                    fetchKnowledgeBaseConfig(companyId, assistant.uid),
                    features.invites
                        ? fetchKnowledgeBaseInvites(companyId, assistant.uid)
                        : Promise.resolve(null)
                ]);
                const cfg = cfgRes.data ?? {};
                const qs = [...Array(6)].map((_, i) =>
                    cfg.assistantSuggestedQuestions && cfg.assistantSuggestedQuestions.length > i
                        ? cfg.assistantSuggestedQuestions[i]
                        : ""
                );
                setPrompt(cfg.prompt ?? "");
                setAssistantTitle(cfg.assistantTitle ?? "");
                setAssistantDescription(cfg.assistantDescription ?? "");
                setQuestions(qs);
                setNegativeAnswer(cfg.negativeAnswer ?? "");
                setResponseInstructions(cfg.responseInstructions ?? "");
                setCoverageDescription(cfg.coverageDescription ?? "");
                setCoverageGenerated(cfg.coverageDescriptionGenerated ?? "");
                setVectorWeight(cfg.vectorWeight ?? 0.5);
                setSearchTopK(cfg.searchTopK ?? 50);
                setSearchResultLimit(cfg.searchResultLimit ?? 10);
                setRrfConstant(cfg.rrfConstant ?? 60);
                setUseReranker(cfg.useReranker ?? true);
                setRerankerModel(cfg.rerankerModel ?? null);
                setBm25Language(cfg.bm25Language ?? "english");
                setChunkSize(cfg.chunkSize ?? undefined);
                setChunkOverlap(cfg.chunkOverlap ?? undefined);
                const procOpts = cfg.processingOptions ?? {};
                setMetadataModel(procOpts.metadataExtractionModel ?? "");
                setMetadataSamplePages(procOpts.metadataSamplePages ?? undefined);
                setRedigestBatchSize(procOpts.redigestBatchSize ?? undefined);
                setVideoChunkSeconds(procOpts.videoChunkSeconds ?? undefined);
                setVideoModel(procOpts.videoTranscriptionModel ?? "");
                setRecognitionMode(cfg.recognitionMode ?? null);
                setPdfRecognitionModel(cfg.pdfRecognitionModel ?? null);
                setThinkingLevel(cfg.thinkingLevel ?? "");
                setEmbeddingModel(cfg.embeddingModel ?? "");
                setInvites(invRes?.data?.items ?? []);
                initial.current = {
                    prompt: cfg.prompt ?? "",
                    assistantTitle: cfg.assistantTitle ?? "",
                    assistantDescription: cfg.assistantDescription ?? "",
                    questions: qs,
                    negativeAnswer: cfg.negativeAnswer ?? "",
                    responseInstructions: cfg.responseInstructions ?? "",
                    coverageDescription: cfg.coverageDescription ?? "",
                    vectorWeight: cfg.vectorWeight ?? 0.5,
                    searchTopK: cfg.searchTopK ?? 50,
                    searchResultLimit: cfg.searchResultLimit ?? 10,
                    rrfConstant: cfg.rrfConstant ?? 60,
                    useReranker: cfg.useReranker ?? true,
                    rerankerModel: cfg.rerankerModel ?? null,
                    bm25Language: cfg.bm25Language ?? "english",
                    chunkSize: cfg.chunkSize ?? undefined,
                    chunkOverlap: cfg.chunkOverlap ?? undefined,
                    recognitionMode: cfg.recognitionMode ?? null,
                    pdfRecognitionModel: cfg.pdfRecognitionModel ?? null,
                    thinkingLevel: cfg.thinkingLevel ?? "",
                    embeddingModel: cfg.embeddingModel ?? "",
                    processingOptions: JSON.stringify(
                        normalizeProcessingOptions(cfg.processingOptions)
                    )
                };
            } catch {
                showToast("Failed to load Assistant settings", 3000);
            } finally {
                setLoading(false);
            }
        })();
    }, [companyId, assistant.uid, features.invites, showToast]);

    useEffect(() => {
        if (!features.embedding) return;
        fetchEmbeddingModels().then(res => setEmbeddingModels(res.data || []));
    }, [features.embedding]);

    const embeddingChanged =
        features.embedding && !loading && embeddingModel !== (initial.current.embeddingModel ?? "");

    const configPatch = (): Record<string, unknown> => {
        const init = initial.current;
        const patch: Record<string, unknown> = {};
        // Presentation (manager+)
        if (assistantTitle !== init.assistantTitle) patch.assistantTitle = trim(assistantTitle);
        if (assistantDescription !== init.assistantDescription)
            patch.assistantDescription = trim(assistantDescription);
        if (JSON.stringify(questions) !== JSON.stringify(init.questions))
            patch.assistantSuggestedQuestions = questions.map(q => trim(q));
        // Send the trimmed value even when empty: `|| undefined` dropped the
        // key from the JSON body, so a CLEARED field silently never saved.
        if (negativeAnswer !== init.negativeAnswer) patch.negativeAnswer = trim(negativeAnswer);
        if (responseInstructions !== init.responseInstructions)
            patch.responseInstructions = trim(responseInstructions);
        // Empty string is meaningful: it reverts the manifest description to
        // the auto-generated text server-side.
        if (coverageDescription !== init.coverageDescription)
            patch.coverageDescription = trim(coverageDescription);
        // Retrieval + Processing (superAdmin — non-superAdmins never diverge
        // from the loaded values, so these stay out of their patches)
        if (vectorWeight !== init.vectorWeight) {
            patch.vectorWeight = vectorWeight;
            patch.bm25Weight = bm25Weight;
        }
        if (searchTopK !== init.searchTopK) patch.searchTopK = searchTopK;
        if (searchResultLimit !== init.searchResultLimit)
            patch.searchResultLimit = searchResultLimit;
        if (rrfConstant !== init.rrfConstant) patch.rrfConstant = rrfConstant;
        if (useReranker !== init.useReranker) patch.useReranker = useReranker;
        if (rerankerModel !== init.rerankerModel) patch.rerankerModel = rerankerModel;
        if (bm25Language !== init.bm25Language) patch.bm25Language = bm25Language;
        if (chunkSize !== init.chunkSize) patch.chunkSize = chunkSize;
        if (chunkOverlap !== init.chunkOverlap) patch.chunkOverlap = chunkOverlap;
        if (recognitionMode !== init.recognitionMode) patch.recognitionMode = recognitionMode;
        if (pdfRecognitionModel !== init.pdfRecognitionModel)
            patch.pdfRecognitionModel = pdfRecognitionModel;
        if (thinkingLevel !== init.thinkingLevel) patch.thinkingLevel = thinkingLevel || null;
        if (embeddingChanged && embeddingModel) patch.embeddingModel = embeddingModel;
        // Processing dials: send the whole object when any dial changed;
        // an all-defaults object clears the column (null).
        const procOpts = normalizeProcessingOptions({
            metadataExtractionModel: metadataModel || undefined,
            videoTranscriptionModel: videoModel || undefined,
            metadataSamplePages,
            redigestBatchSize,
            videoChunkSeconds
        });
        if (JSON.stringify(procOpts) !== ((init.processingOptions as string) ?? "{}")) {
            patch.processingOptions = Object.keys(procOpts).length ? procOpts : null;
        }
        return patch;
    };

    const kbPatch = (): Record<string, unknown> => {
        const patch: Record<string, unknown> = {};
        if (displayName !== (assistant.displayName ?? "")) patch.displayName = trim(displayName);
        if (description !== (assistant.description ?? "")) patch.description = trim(description);
        if (features.prompt && prompt !== ((initial.current.prompt as string) ?? ""))
            patch.prompt = prompt;
        return patch;
    };

    const hasChanges = () =>
        Object.keys(configPatch()).length > 0 ||
        Object.keys(kbPatch()).length > 0 ||
        !!logoFile ||
        removeLogo;

    const save = async () => {
        // Changing the embedding model wipes every chunk — confirm first.
        if (embeddingChanged && !confirmEmbedding) {
            setConfirmEmbedding(true);
            return;
        }
        setConfirmEmbedding(false);
        setSaving(true);
        try {
            const kbData = kbPatch();
            if (logoFile) {
                const logoRes = await fetchKBLogoUrl(companyId, assistant.uid, {
                    originalname: logoFile.name,
                    mimetype: logoFile.type,
                    size: logoFile.size
                });
                const { headers, publicUrl, url } = logoRes.data;
                await uploadFile(url, logoFile, { headers });
                kbData.logoUrl = publicUrl;
            } else if (removeLogo) {
                kbData.logoUrl = null;
            }
            if (Object.keys(kbData).length) {
                await patchKnowledgeBase(companyId, assistant.uid, kbData);
            }
            const cfgData = configPatch();
            if (Object.keys(cfgData).length) {
                await patchKnowledgeBaseConfig(companyId, assistant.uid, cfgData);
            }
            showToast("Assistant updated", 3000);
            onSaved();
        } catch (e: any) {
            showToast(e?.response?.data?.message ?? "Failed to update Assistant", 3000);
        } finally {
            setSaving(false);
        }
    };

    const inviteLink = (code: string, stripAdmin: boolean) => {
        const host = stripAdmin ? window.location.host.replace("admin.", "") : window.location.host;
        return `${window.location.protocol}//${host}/registration?invite=${code}`;
    };
    const managerInvite = invites.find(i => i.role === UserRole.manager);
    const userInvite = invites.find(i => i.role === UserRole.user);
    const copyInvite = (link: string) => {
        navigator.clipboard.writeText(link);
        showToast(
            "Invite link copied.\nRemember that these links provide general access to the assistant. Only share these links with trusted parties unless general public access of the assistant is desired.",
            4000
        );
    };

    const tabLabel = (t: string) =>
        ({
            profile: "Profile",
            presentation: "Chat Settings",
            answers: "Answer style",
            prompt: "Prompt",
            retrieval: "Retrieval",
            processing: "Processing",
            invites: "Invites"
        })[t] ?? t;

    return (
        <ModalTemplate
            open
            width={900}
            ariaLabel={`Edit Assistant ${assistant.displayName || assistant.name}`}
            handleClose={() => {
                if (!saving) onClose();
            }}
        >
            <DialHelpProvider helpId={dialHelpId}>
                <div className="assistants-admin-modal">
                    <h2 className="assistants-admin-modal__title">
                        {assistant.displayName || assistant.name}
                    </h2>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        className="assistants-admin-modal__tabs"
                    >
                        {tabs.map(t => (
                            <Tab key={t} value={t} label={tabLabel(t)} />
                        ))}
                    </Tabs>
                    <div className="assistants-admin-modal__body">
                        {tab === "profile" && (
                            <>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-displayName`}>Name</label>
                                    <input
                                        id={`${fieldId}-displayName`}
                                        type="text"
                                        value={displayName}
                                        placeholder="Assistant name"
                                        onChange={e => setDisplayName(e.target.value)}
                                    />
                                </div>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-description`}>Description</label>
                                    <textarea
                                        id={`${fieldId}-description`}
                                        value={description}
                                        placeholder="Description shown to users"
                                        onChange={e => setDescription(e.target.value)}
                                    />
                                </div>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-coverage`}>
                                        Coverage description
                                    </label>
                                    <textarea
                                        id={`${fieldId}-coverage`}
                                        value={coverageDescription}
                                        placeholder={
                                            coverageGenerated ||
                                            "Auto-generated from document metadata once sources are added"
                                        }
                                        onChange={e => setCoverageDescription(e.target.value)}
                                    />
                                    <p className="assistants-admin-field__hint">
                                        Tells the coordinator what this Assistant&apos;s documents
                                        cover, so &quot;Use All Assistants&quot; questions route
                                        here when they should. Leave empty to use the auto-generated
                                        summary (shown above as the placeholder).
                                    </p>
                                </div>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-logo`}>Logo</label>
                                    <input
                                        id={`${fieldId}-logo`}
                                        className="assistants-admin-field__file"
                                        ref={logoInputRef}
                                        type="file"
                                        accept=".png,.jpg,.jpeg,.svg,.webp"
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            setLogoFile(e.target.files?.[0]);
                                            setRemoveLogo(false);
                                        }}
                                    />
                                    <div className="assistants-admin-field__buttons">
                                        <Button
                                            className="assistants-admin-modal__tool"
                                            onClick={() => logoInputRef.current?.click()}
                                        >
                                            {logoFile?.name ?? "Choose file to update logo"}
                                        </Button>
                                        {!!assistant.logoUrl && !logoFile && (
                                            <Button
                                                className="assistants-admin-modal__cancel"
                                                variant="text"
                                                onClick={() => setRemoveLogo(v => !v)}
                                            >
                                                {removeLogo ? "Undo remove" : "Remove logo"}
                                            </Button>
                                        )}
                                    </div>
                                    {removeLogo && !logoFile && (
                                        <p className="assistants-admin-field__hint">
                                            The current logo will be removed when you save.
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                        {tab === "presentation" && (
                            <>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-title`}>Header</label>
                                    <textarea
                                        id={`${fieldId}-title`}
                                        value={assistantTitle}
                                        maxLength={60}
                                        placeholder="Enter Assistant header"
                                        onChange={e => setAssistantTitle(e.target.value)}
                                    />
                                </div>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-adesc`}>Description</label>
                                    <textarea
                                        id={`${fieldId}-adesc`}
                                        value={assistantDescription}
                                        maxLength={200}
                                        placeholder="Enter assistant description"
                                        onChange={e => setAssistantDescription(e.target.value)}
                                    />
                                </div>
                                {questions.map((q, i) => (
                                    <div key={i} className="assistants-admin-field">
                                        <label
                                            htmlFor={`${fieldId}-q${i}`}
                                        >{`Suggested question ${i + 1}`}</label>
                                        <textarea
                                            id={`${fieldId}-q${i}`}
                                            value={q}
                                            maxLength={SUGGESTED_QUESTION_LIMITS[i]}
                                            placeholder={`Enter assistant question ${i + 1}`}
                                            onChange={e =>
                                                setQuestions(prev =>
                                                    prev.map((item, j) =>
                                                        j === i ? e.target.value : item
                                                    )
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </>
                        )}
                        {tab === "answers" && (
                            <>
                                <p className="assistants-admin-field__hint">
                                    These shape every answer this Assistant gives — its wording when
                                    the documents do not cover a question, and its formatting
                                    conventions. On agentic chat they flow into the serving
                                    agent&apos;s persona automatically.
                                </p>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-negative`}>Negative answer</label>
                                    <textarea
                                        id={`${fieldId}-negative`}
                                        className="assistants-admin-field__tall"
                                        value={negativeAnswer}
                                        placeholder="Thank you for your question! Unfortunately, I don't have the specific information you're looking for at the moment."
                                        onChange={e => setNegativeAnswer(e.target.value)}
                                    />
                                </div>
                                <div className="assistants-admin-field">
                                    <label htmlFor={`${fieldId}-instructions`}>
                                        Response instructions
                                    </label>
                                    <textarea
                                        id={`${fieldId}-instructions`}
                                        className="assistants-admin-field__tall"
                                        value={responseInstructions}
                                        placeholder={
                                            "Decide the best format for the answer being provided.\nTry to provide full answers using all relevant information from the context."
                                        }
                                        onChange={e => setResponseInstructions(e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                        {tab === "prompt" && (
                            <div className="assistants-admin-field">
                                <label htmlFor={`${fieldId}-prompt`}>System prompt</label>
                                <textarea
                                    id={`${fieldId}-prompt`}
                                    className="assistants-admin-field__prompt"
                                    value={prompt}
                                    onChange={e => setPrompt(e.target.value)}
                                />
                            </div>
                        )}
                        {tab === "retrieval" && (
                            <>
                                <DialField
                                    dial="searchWeights"
                                    inline={false}
                                    changed={vectorWeight !== 0.5}
                                    onReset={() => setVectorWeight(0.5)}
                                >
                                    <div className="assistants-admin__slider">
                                        <div className="assistants-admin__slider-labels">
                                            <span>BM25 (Keyword): {bm25Weight.toFixed(1)}</span>
                                            <span>
                                                Vector (Semantic): {vectorWeight.toFixed(1)}
                                            </span>
                                        </div>
                                        <Slider
                                            aria-label="Search weight, keyword to semantic"
                                            value={vectorWeight}
                                            onChange={(_, val) => setVectorWeight(val as number)}
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            marks={[
                                                { value: 0, label: "Keyword" },
                                                { value: 0.5, label: "Balanced" },
                                                { value: 1, label: "Semantic" }
                                            ]}
                                        />
                                    </div>
                                </DialField>
                                <DialField
                                    dial="searchTopK"
                                    htmlFor={`${fieldId}-topK`}
                                    changed={searchTopK !== 50}
                                    onReset={() => setSearchTopK(50)}
                                >
                                    <input
                                        id={`${fieldId}-topK`}
                                        type="number"
                                        min={1}
                                        aria-describedby={dialHelpId}
                                        value={searchTopK}
                                        onChange={e => {
                                            const v = parseInt(e.target.value, 10);
                                            if (!isNaN(v)) setSearchTopK(v);
                                        }}
                                    />
                                </DialField>
                                <DialField
                                    dial="searchResultLimit"
                                    htmlFor={`${fieldId}-resultLimit`}
                                    changed={searchResultLimit !== 10}
                                    onReset={() => setSearchResultLimit(10)}
                                >
                                    <input
                                        id={`${fieldId}-resultLimit`}
                                        type="number"
                                        min={1}
                                        aria-describedby={dialHelpId}
                                        value={searchResultLimit}
                                        onChange={e => {
                                            const v = parseInt(e.target.value, 10);
                                            if (!isNaN(v)) setSearchResultLimit(v);
                                        }}
                                    />
                                </DialField>
                                <DialField
                                    dial="rrfConstant"
                                    htmlFor={`${fieldId}-rrf`}
                                    changed={rrfConstant !== 60}
                                    onReset={() => setRrfConstant(60)}
                                >
                                    <input
                                        id={`${fieldId}-rrf`}
                                        type="number"
                                        min={1}
                                        aria-describedby={dialHelpId}
                                        value={rrfConstant}
                                        onChange={e => {
                                            const v = parseInt(e.target.value, 10);
                                            if (!isNaN(v)) setRrfConstant(v);
                                        }}
                                    />
                                </DialField>
                                <DialField
                                    dial="useReranker"
                                    changed={useReranker !== true}
                                    onReset={() => setUseReranker(true)}
                                >
                                    <Switch
                                        checked={useReranker}
                                        onChange={(_, c) => setUseReranker(c)}
                                        inputProps={{
                                            "aria-label": "Use reranker",
                                            "aria-describedby": dialHelpId
                                        }}
                                    />
                                </DialField>
                                <DialField
                                    dial="rerankerModel"
                                    htmlFor={`${fieldId}-rerankerModel`}
                                    changed={rerankerModel !== null}
                                    onReset={() => setRerankerModel(null)}
                                >
                                    <select
                                        id={`${fieldId}-rerankerModel`}
                                        value={rerankerModel ?? ""}
                                        disabled={!useReranker}
                                        aria-describedby={dialHelpId}
                                        onChange={e =>
                                            setRerankerModel(
                                                e.target.value === "" ? null : e.target.value
                                            )
                                        }
                                    >
                                        <option value="">Default</option>
                                        {RERANKER_MODELS.map(m => (
                                            <option key={m} value={m}>
                                                {m}
                                            </option>
                                        ))}
                                    </select>
                                </DialField>
                                <DialField
                                    dial="bm25Language"
                                    htmlFor={`${fieldId}-bm25Lang`}
                                    changed={bm25Language !== "english"}
                                    onReset={() => setBm25Language("english")}
                                >
                                    <select
                                        id={`${fieldId}-bm25Lang`}
                                        value={bm25Language}
                                        aria-describedby={dialHelpId}
                                        onChange={e => setBm25Language(e.target.value)}
                                    >
                                        {BM25_LANGUAGES.map(lang => (
                                            <option key={lang} value={lang}>
                                                {lang.charAt(0).toUpperCase() + lang.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </DialField>
                            </>
                        )}
                        {tab === "processing" && (
                            <>
                                {features.recognition && (
                                    <>
                                        <div
                                            className="assistants-admin-modal__section"
                                            role="heading"
                                            aria-level={3}
                                        >
                                            Recognition
                                        </div>
                                        <DialField
                                            dial="recognitionMode"
                                            htmlFor={`${fieldId}-mode`}
                                            changed={recognitionMode !== null}
                                            onReset={() => setRecognitionMode(null)}
                                        >
                                            <select
                                                id={`${fieldId}-mode`}
                                                value={recognitionMode ?? ""}
                                                aria-describedby={dialHelpId}
                                                onChange={e =>
                                                    setRecognitionMode(
                                                        e.target.value === ""
                                                            ? null
                                                            : e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">Default</option>
                                                <option value="single_page">Single Page</option>
                                                <option value="rich_document">Rich Document</option>
                                            </select>
                                        </DialField>
                                        <DialField
                                            dial="pdfRecognitionModel"
                                            htmlFor={`${fieldId}-pdfModel`}
                                            changed={pdfRecognitionModel !== null}
                                            onReset={() => setPdfRecognitionModel(null)}
                                        >
                                            <select
                                                id={`${fieldId}-pdfModel`}
                                                value={pdfRecognitionModel ?? ""}
                                                aria-describedby={dialHelpId}
                                                onChange={e =>
                                                    setPdfRecognitionModel(
                                                        e.target.value === ""
                                                            ? null
                                                            : e.target.value
                                                    )
                                                }
                                            >
                                                <option value="">Default</option>
                                                {PDF_MODELS.map(m => (
                                                    <option key={m} value={m}>
                                                        {m}
                                                    </option>
                                                ))}
                                            </select>
                                        </DialField>
                                        <DialField
                                            dial="videoTranscriptionModel"
                                            htmlFor={`${fieldId}-videoModel`}
                                            changed={videoModel !== ""}
                                            onReset={() => setVideoModel("")}
                                        >
                                            <select
                                                id={`${fieldId}-videoModel`}
                                                value={videoModel}
                                                aria-describedby={dialHelpId}
                                                onChange={e => setVideoModel(e.target.value)}
                                            >
                                                <option value="">Default</option>
                                                {VIDEO_MODELS.map(m => (
                                                    <option key={m} value={m}>
                                                        {m}
                                                    </option>
                                                ))}
                                            </select>
                                        </DialField>
                                        <DialField
                                            dial="thinkingLevel"
                                            htmlFor={`${fieldId}-thinkingLevel`}
                                            changed={thinkingLevel !== ""}
                                            onReset={() => setThinkingLevel("")}
                                        >
                                            <select
                                                id={`${fieldId}-thinkingLevel`}
                                                value={thinkingLevel}
                                                aria-describedby={dialHelpId}
                                                onChange={e => setThinkingLevel(e.target.value)}
                                            >
                                                <option value="">Model default</option>
                                                {THINKING_LEVELS.map(level => (
                                                    <option key={level.value} value={level.value}>
                                                        {level.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </DialField>
                                    </>
                                )}
                                {features.chunking && (
                                    <>
                                        <div
                                            className="assistants-admin-modal__section"
                                            role="heading"
                                            aria-level={3}
                                        >
                                            Chunking
                                        </div>
                                        <DialField
                                            dial="chunkSize"
                                            htmlFor={`${fieldId}-chunkSize`}
                                            changed={chunkSize !== undefined}
                                            onReset={() => setChunkSize(undefined)}
                                        >
                                            <input
                                                id={`${fieldId}-chunkSize`}
                                                type="number"
                                                min={100}
                                                step={100}
                                                placeholder="Default"
                                                aria-describedby={dialHelpId}
                                                value={chunkSize ?? ""}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    if (raw === "") setChunkSize(undefined);
                                                    else {
                                                        const v = parseInt(raw, 10);
                                                        if (!isNaN(v)) setChunkSize(v);
                                                    }
                                                }}
                                            />
                                        </DialField>
                                        <DialField
                                            dial="chunkOverlap"
                                            htmlFor={`${fieldId}-chunkOverlap`}
                                            changed={chunkOverlap !== undefined}
                                            onReset={() => setChunkOverlap(undefined)}
                                        >
                                            <input
                                                id={`${fieldId}-chunkOverlap`}
                                                type="number"
                                                min={0}
                                                step={50}
                                                placeholder="Default"
                                                aria-describedby={dialHelpId}
                                                value={chunkOverlap ?? ""}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    if (raw === "") setChunkOverlap(undefined);
                                                    else {
                                                        const v = parseInt(raw, 10);
                                                        if (!isNaN(v)) setChunkOverlap(v);
                                                    }
                                                }}
                                            />
                                        </DialField>
                                    </>
                                )}
                                {features.metadata && (
                                    <>
                                        <div
                                            className="assistants-admin-modal__section"
                                            role="heading"
                                            aria-level={3}
                                        >
                                            Metadata extraction
                                        </div>
                                        <DialField
                                            dial="metadataExtractionModel"
                                            htmlFor={`${fieldId}-metaModel`}
                                            changed={metadataModel !== ""}
                                            onReset={() => setMetadataModel("")}
                                        >
                                            <select
                                                id={`${fieldId}-metaModel`}
                                                value={metadataModel}
                                                aria-describedby={dialHelpId}
                                                onChange={e => setMetadataModel(e.target.value)}
                                            >
                                                <option value="">Default</option>
                                                {METADATA_MODELS.map(m => (
                                                    <option key={m} value={m}>
                                                        {m}
                                                    </option>
                                                ))}
                                            </select>
                                        </DialField>
                                        <DialField
                                            dial="metadataSamplePages"
                                            htmlFor={`${fieldId}-metaPages`}
                                            changed={metadataSamplePages !== undefined}
                                            onReset={() => setMetadataSamplePages(undefined)}
                                        >
                                            <input
                                                id={`${fieldId}-metaPages`}
                                                type="number"
                                                min={1}
                                                max={10}
                                                placeholder="Default"
                                                aria-describedby={dialHelpId}
                                                value={metadataSamplePages ?? ""}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    if (raw === "")
                                                        setMetadataSamplePages(undefined);
                                                    else {
                                                        const v = parseInt(raw, 10);
                                                        if (!isNaN(v)) setMetadataSamplePages(v);
                                                    }
                                                }}
                                            />
                                        </DialField>
                                    </>
                                )}
                                {features.maintenance && (
                                    <>
                                        <div
                                            className="assistants-admin-modal__section"
                                            role="heading"
                                            aria-level={3}
                                        >
                                            Maintenance
                                        </div>
                                        <DialField
                                            dial="redigestBatchSize"
                                            htmlFor={`${fieldId}-redigestBatch`}
                                            changed={redigestBatchSize !== undefined}
                                            onReset={() => setRedigestBatchSize(undefined)}
                                        >
                                            <input
                                                id={`${fieldId}-redigestBatch`}
                                                type="number"
                                                min={1}
                                                max={50}
                                                placeholder="Default"
                                                aria-describedby={dialHelpId}
                                                value={redigestBatchSize ?? ""}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    if (raw === "") setRedigestBatchSize(undefined);
                                                    else {
                                                        const v = parseInt(raw, 10);
                                                        if (!isNaN(v)) setRedigestBatchSize(v);
                                                    }
                                                }}
                                            />
                                        </DialField>
                                        <DialField
                                            dial="videoChunkSeconds"
                                            htmlFor={`${fieldId}-videoSeconds`}
                                            changed={videoChunkSeconds !== undefined}
                                            onReset={() => setVideoChunkSeconds(undefined)}
                                        >
                                            <input
                                                id={`${fieldId}-videoSeconds`}
                                                type="number"
                                                min={60}
                                                max={900}
                                                step={30}
                                                placeholder="Default"
                                                aria-describedby={dialHelpId}
                                                value={videoChunkSeconds ?? ""}
                                                onChange={e => {
                                                    const raw = e.target.value;
                                                    if (raw === "") setVideoChunkSeconds(undefined);
                                                    else {
                                                        const v = parseInt(raw, 10);
                                                        if (!isNaN(v)) setVideoChunkSeconds(v);
                                                    }
                                                }}
                                            />
                                        </DialField>
                                    </>
                                )}
                                {features.embedding && (
                                    <>
                                        <div
                                            className="assistants-admin-modal__section"
                                            role="heading"
                                            aria-level={3}
                                        >
                                            Embedding
                                        </div>
                                        <DialField
                                            dial="embeddingModel"
                                            htmlFor={`${fieldId}-embedding`}
                                            changed={false}
                                        >
                                            <select
                                                id={`${fieldId}-embedding`}
                                                value={embeddingModel}
                                                aria-describedby={dialHelpId}
                                                onChange={e => setEmbeddingModel(e.target.value)}
                                            >
                                                {embeddingModel === "" && (
                                                    <option value="">Default</option>
                                                )}
                                                {embeddingModels.map(m => (
                                                    <option key={m} value={m}>
                                                        {m}
                                                    </option>
                                                ))}
                                            </select>
                                        </DialField>
                                        {embeddingChanged && (
                                            <p
                                                className="assistants-admin-modal__warning"
                                                role="alert"
                                            >
                                                Changing the embedding model deletes every stored
                                                chunk and requires a full re-embedding of all
                                                documents.
                                            </p>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {tab === "invites" && (
                            <>
                                {[
                                    { label: "Manager", invite: managerInvite, strip: false },
                                    { label: "User", invite: userInvite, strip: true }
                                ].map(({ label, invite, strip }) => {
                                    const link = invite ? inviteLink(invite.code, strip) : "";
                                    return (
                                        <div key={label} className="assistants-admin-field">
                                            <span className="assistants-admin-field__label">
                                                {label}
                                            </span>
                                            <button
                                                type="button"
                                                className="assistants-admin__invite"
                                                aria-label={`Copy ${label} invite link`}
                                                disabled={!link}
                                                onClick={() => link && copyInvite(link)}
                                            >
                                                <span className="assistants-admin__invite-text">
                                                    {link || "No invite available"}
                                                </span>
                                                <SvgIcon
                                                    className="assistants-admin__invite-icon"
                                                    component={CopyIcon}
                                                    inheritViewBox
                                                />
                                            </button>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                    {(tab === "retrieval" || tab === "processing") && <DialHelpStrip />}
                    <div className="assistants-admin-modal__actions">
                        <Button className="assistants-admin-modal__cancel" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            className="assistants-admin-modal__submit"
                            onClick={save}
                            disabled={saving || loading || !hasChanges()}
                        >
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
            </DialHelpProvider>
            <ConfirmationPopup
                open={confirmEmbedding}
                type={ConfirmationPopupType.dangerous}
                title="Change embedding model?"
                description="Changing the embedding model deletes every stored chunk and flags all documents for full re-embedding. Search will be degraded until reindexing completes."
                actionName="Change"
                showCancelButton
                handleAction={save}
                handleClose={() => setConfirmEmbedding(false)}
            />
        </ModalTemplate>
    );
}
